import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import MaskedReveal from "./MaskedReveal";

const ease = [0.16, 1, 0.3, 1];
const HERO_IMG =
  "https://images.unsplash.com/photo-1708011108776-45ad9e625269?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxjaW5lbWF0aWMlMjBmaXRuZXNzJTIwcG9ydHJhaXQlMjBkYXJrfGVufDB8fHx8MTc4Mzk2NTk1M3ww&ixlib=rb-4.1.0&q=85";

export default function Hero({ onStart }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1.08, 1.2]);
  const opacityOverlay = useTransform(scrollYProgress, [0, 0.8], [0.55, 0.95]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative w-full min-h-[100svh] overflow-hidden bg-obsidian"
    >
      {/* Parallax background image */}
      <motion.div
        style={{ y: yImg, scale: scaleImg }}
        className="absolute inset-0 will-change-transform"
        aria-hidden
      >
        <img
          src={HERO_IMG}
          alt=""
          className="w-full h-full object-cover object-[50%_30%]"
          draggable={false}
        />
      </motion.div>

      {/* Overlays */}
      <motion.div
        style={{ opacity: opacityOverlay }}
        className="absolute inset-0 bg-gradient-to-b from-obsidian/60 via-obsidian/50 to-obsidian"
      />
      <div className="absolute inset-0 spotlight pointer-events-none" />

      {/* Side rails */}
      <div className="absolute inset-y-0 left-6 md:left-12 flex items-end pb-10 z-10">
        <motion.span
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.4, ease }}
          className="[writing-mode:vertical-rl] rotate-180 text-[0.65rem] uppercase tracking-[0.5em] text-cream/50 font-light"
        >
          Studio · Conegliano · Est. MMXVIII
        </motion.span>
      </div>
      <div className="absolute inset-y-0 right-6 md:right-12 flex items-end pb-10 z-10">
        <motion.span
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.5, ease }}
          className="text-[0.65rem] uppercase tracking-[0.45em] text-cream/50 font-light"
        >
          N° 001 — Manifesto
        </motion.span>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 pt-40 md:pt-48 pb-24 min-h-[100svh] flex flex-col justify-between">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4, ease }}
            className="flex items-center gap-4 mb-10 md:mb-14"
          >
            <span className="w-10 h-px bg-gold" />
            <span className="text-[0.7rem] uppercase tracking-[0.42em] text-gold">
              Consulenza gratuita · Solo su invito
            </span>
          </motion.div>

          <MaskedReveal
            as="h1"
            className="font-serif font-light text-cream leading-[0.98] tracking-[-0.02em] text-[15vw] md:text-[9.5vw] lg:text-[8.5vw] max-w-[15ch]"
            lines={[
              <>Trasforma il tuo</>,
              <>
                corpo. <em className="italic text-gold/90 font-light">Cambia</em>
              </>,
              <>la tua vita.</>,
            ]}
            delay={0.6}
            stagger={0.16}
          />
        </div>

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-10 mt-16">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.6, ease }}
            className="max-w-md text-cream/70 font-light text-base md:text-lg leading-relaxed"
          >
            BBfit è uno studio privato di allenamento personale. Un metodo su
            misura, un percorso guidato, un risultato reale — <em className="italic text-cream/90">senza scorciatoie</em>.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.75, ease }}
            className="flex flex-col items-start md:items-end gap-4"
          >
            <button
              type="button"
              onClick={onStart}
              data-testid="hero-start-quiz"
              className="group relative overflow-hidden bg-gold hover:bg-gold-hover text-obsidian px-10 py-5 font-sans uppercase tracking-[0.24em] text-[0.75rem] font-semibold transition-colors duration-500"
            >
              <span className="relative z-10 flex items-center gap-3">
                Inizia il quiz
                <svg width="18" height="10" viewBox="0 0 18 10" fill="none" className="transition-transform duration-500 group-hover:translate-x-1">
                  <path d="M0 5H17M17 5L13 1M17 5L13 9" stroke="currentColor" strokeWidth="1.2" />
                </svg>
              </span>
            </button>
            <span className="text-[0.65rem] uppercase tracking-[0.32em] text-cream/50">
              90 secondi · 5 domande
            </span>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
      >
        <span className="text-[0.6rem] uppercase tracking-[0.4em] text-cream/40">scorri</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          className="w-px h-8 bg-cream/40"
        />
      </motion.div>
    </section>
  );
}
